import React, {
  useEffect,
  useMemo,
  useState
} from 'react';
import { FileUploadHub, FileListFile } from '@project/components';
import PropTypes from 'prop-types';
import { Button, Image } from 'react-bootstrap';
import uploadImg from './../../assets/images/button-icons/upload.svg';
import axios from '../../utils/axios';
import buttonStyles from '../UploadButton/UploadButton.module.css';

const InternalFilesUploader = ({
  buttonLabel,
  errorMessagePrefix,
  modalTitle,
  profileCompany,
  profileFileBundle,
  profileFileBundleKey,
  profileGuid,
  profileProperty,
  updateField
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [fileToDelete, setFileToDelete] = useState(null);
  const [fileToUpload, setFileToUpload] = useState(null);
  const [fileManagerError, setFileManagerError] = useState(null);
  // TODO: this is a workaround to prevent the BasicInfoForm component to re-render.
  // The re-render destroys this uploader component, closing the modal in the process.
  // To prevent that, we shouldn't call `updateField` as soon as a file is uploaded or
  // deleted, but instead cache the field changes and `updateField` when we close the modal.
  // That way we can safely exit the modal and then the BasicInfoForm can be destroyed
  // without breaking this component.
  // The logic here is that, when this component gets mounted, it receives the initial form
  // value and we use that to seed the cache. Then we add and remove files, updating the cache
  // and, when we close the modal, call `updateField` with `fileBundleCache` as a source
  // of truth.
  const [fileBundleCache, setFileBundleCache] = useState(profileFileBundle);

  // The object files stored in the space profile object are
  // compliant with the API JSON payload, but for the component
  // that renders the list on a table, they need to follow an
  // specific structure. That's why it's being transformed into an
  // array of instances of FileListFile and memoized.
  const fileList = useMemo(() => {
    const isValidFile = fileJson => Object.keys(fileJson).length !== 0;
    const transformFileJson = fileJson => new FileListFile({
      id: fileJson.guid,
      name: fileJson.name,
      size: fileJson.size,
      uploadDate: fileJson.upload_date,
      uploadUser: fileJson.upload_user,
      url: fileJson.url
    });

    return fileBundleCache.filter(isValidFile).map(transformFileJson);
  }, [fileBundleCache]);

  useEffect(() => {
    const uploadFile = async () => {
      if (!fileToUpload) {
        return;
      }

      setIsUploading(true);
      setFileManagerError(null);

      const endpoint = `/space-profiles/${profileGuid}/file_bundle/${profileFileBundleKey}`;
      const requestConfig = {
        params: {
          company: profileCompany,
          property: profileProperty
        }
      };
      const data = new FormData();
      data.append('file', fileToUpload);

      try {
        const result = await axios.post(endpoint, data, requestConfig);
        // Keep the cache updated based on the up-to-date object state coming from the backend
        setFileBundleCache(result.data.record.basic_info[profileFileBundleKey]);
      } catch (e) {
        setFileManagerError(`${errorMessagePrefix}: ${e.message}`);
      }

      setFileToUpload(null);
      setIsUploading(false);
    };

    uploadFile();
  }, [errorMessagePrefix, fileToUpload, profileCompany, profileProperty, profileFileBundleKey, profileGuid, updateField]);

  useEffect(() => {
    const deleteFile = async () => {
      if (!fileToDelete) {
        return;
      }

      setFileManagerError(null);

      const endpoint = `/space-profiles/${profileGuid}/file_bundle/${profileFileBundleKey}/${fileToDelete.id}`;
      const requestConfig = {
        params: {
          company: profileCompany,
          property: profileProperty
        }
      };

      try {
        const result = await axios.delete(endpoint, requestConfig);
        // Keep the cache updated based on the up-to-date object state coming from the backend
        setFileBundleCache(result.data.record.basic_info[profileFileBundleKey]);
      } catch (e) {
        setFileManagerError(`${errorMessagePrefix}: ${e.message}`);
      }

      setFileToDelete(null);
    };

    deleteFile();
  }, [errorMessagePrefix, fileToDelete, profileCompany, profileFileBundleKey, profileGuid, profileProperty, updateField]);

  // TODO: this is a workaround to prevent the BasicInfoForm component to re-render.
  // Refer to `fileBundleCache` state initialization above for more information.
  const syncFileBundleChanges = () => {
    const updatedFieldOptions = {
      target: {
        type: 'array',
        value: fileBundleCache
      }
    };

    updateField(profileFileBundleKey, updatedFieldOptions, null, true);
  };

  const closeUploaderModal = () => {
    setIsModalOpen(false);
    syncFileBundleChanges();
  };
  const onFileSelected = (_, raw) => setFileToUpload(raw);
  const openUploaderModal = () => setIsModalOpen(true);

  return (
    <>
      <Button
        className={`border-0 d-flex ${buttonStyles.uploadButton}`}
        onClick={openUploaderModal}
        disabled={isModalOpen}
        variant="secondary"
      >
        <Image src={uploadImg} className="mr-2" alt={buttonLabel} />
        <span>{buttonLabel}</span>
      </Button>
      <FileUploadHub
        errors={fileManagerError}
        files={fileList}
        isUploading={isUploading}
        onClose={closeUploaderModal}
        onFileSelected={onFileSelected}
        onDelete={setFileToDelete}
        show={isModalOpen}
        title={modalTitle}
      />
    </>
  );
};

InternalFilesUploader.propTypes = {
  /** Label for the main button that opens the modal */
  buttonLabel: PropTypes.string,
  /**
   * Prefix for the errors displayed by the uploader.
   * Defaults to "Error".
   */
  errorMessagePrefix: PropTypes.string,
  /** File uploader modal title */
  modalTitle: PropTypes.string,
  /** Required to send files to the backend */
  profileCompany: PropTypes.string.isRequired,
  /**
   * Bundle of files previously uploaded.
   * If the profile is new and doesn't have a bundle,
   * an empty array will be used as a default value.
   */
  profileFileBundle: PropTypes.arrayOf(PropTypes.object),
  /** Key used to identify the bundle inside the space profile object */
  profileFileBundleKey: PropTypes.string.isRequired,
  /** Required to send files to the backend */
  profileGuid: PropTypes.string.isRequired,
  /** Required to send files to the backend */
  profileProperty: PropTypes.string.isRequired,
  /**
   * Helper function that allows this component to mutate the
   * space profile object when files are uploaded and deleted.
   */
  updateField: PropTypes.func.isRequired
};

InternalFilesUploader.defaultProps = {
  buttonLabel: 'Upload Internal Files',
  errorMessagePrefix: 'Error',
  modalTitle: 'File Manager',
  profileFileBundle: []
};

export default InternalFilesUploader;